import React, { useRef, useState } from 'react';
import { addMonths, addYears, isAfter, isBefore, parseISO, startOfDay, subDays } from 'date-fns';
import { Collapse, styled } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { Colors } from 'library';
import {
    Alert,
    DatePicker,
    Divider,
    IconButton,
    Label,
    Link,
    Modal,
    PillButton,
    RadioGroup,
    Row,
    Select,
    Stack,
    Text,
    Title,
} from 'library/ui';
import { client, consoleLogInDev } from 'shared';
import { extractErrorMessage } from 'shared/ApiClient';
import { ShiftProviderUser } from 'models';
import RevealModal from 'parent-portal/jobs/upcoming/BusinessCalendar/ShiftCard/RevealModal';

const CLEARINGHOUSE_URL = 'https://crw.flclearinghouse.com/';

const DCF_STATUS_OPTIONS = [
    { key: 'not eligible', value: 'not eligible', label: 'Not Eligible' },
    { key: 'screening in process', value: 'screening in process', label: 'Screening in Process' },
    { key: 'resubmission in process', value: 'resubmission in process', label: 'Resubmission in Process' },
    { key: 'agency review in process', value: 'agency review in process', label: 'Agency Review in Process' },
    { key: 'agency review required', value: 'agency review required', label: 'Agency Review Required' },
    { key: 'resubmission required', value: 'resubmission required', label: 'Resubmission Required' },
    {
        key: 'additional information requested from applicant',
        value: 'additional information requested from applicant',
        label: 'Additional Information Requested from Applicant',
    },
    { key: 'a new screening is required', value: 'a new screening is required', label: 'A New Screening is Required' },
];
const STATUS_REQUIRING_REVIEW = ['agency review required', 'resubmission required'];
const STATUSES_REQUIRING_EXPIRATION = [
    'agency review required',
    'agency review in process',
    'resubmission required',
    'resubmission in process',
];
const REVIEW_NAMES: Record<string, string> = {
    'agency review required': 'Agency Review',
    'resubmission required': 'Resubmission',
};

export function dcfStatusLabel(value: string) {
    return DCF_STATUS_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export type BookResult = {
    status: 'booked' | 'hiring_group' | 'unavailable' | 'not_eligible';
    reason?: 'last_minute' | 'stale_application';
};

export type VerificationForm = {
    eligibility_date: string;
    expiration_date: string;
    screening_document?: File;
    clearinghouse_status: string;
    eligibility_status?: string;
    initiated_review?: boolean;
};

export function BookResultModal({
    workerName,
    result,
    onClose,
}: {
    workerName: string;
    result: BookResult;
    onClose: () => void;
}) {
    const titles = {
        booked: 'Added to Schedule',
        hiring_group: 'Confirmation Needed',
        unavailable: 'No Longer Available',
        not_eligible: 'Cannot Book Right Now',
    };
    const messages = {
        booked: `${workerName} has been added to your schedule successfully!`,
        hiring_group:
            result.reason === 'last_minute'
                ? `Because this shift is last minute, we need ${workerName} to confirm they can make it. We've sent them a request to accept and will add them to your schedule as soon as they do.`
                : `Because ${workerName} applied more than a week ago, we need them to confirm they're still available. We've sent them a request to accept and will add them to your schedule as soon as they do.`,
        unavailable: `Unfortunately, ${workerName} is no longer available for these shifts.`,
        not_eligible: `You cannot currently book ${workerName} based on their Clearinghouse status. We've saved what you found.`,
    };
    return (
        <Modal title={titles[result.status]} onClose={onClose} width={440}>
            <Stack gap="md">
                <Text>{messages[result.status]}</Text>
                <Row justify="end" fullWidth>
                    <PillButton onClick={onClose}>Done</PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}

const LabeledField = ({
    label,
    help,
    image,
    required,
    children,
}: {
    label: string;
    help: React.ReactNode;
    image?: string;
    required?: boolean;
    children: React.ReactNode;
}) => {
    const [helpOpen, setHelpOpen] = useState(false);
    return (
        <Stack gap="sm">
            <Row gap="sm" align="center">
                <Label>
                    {label}
                    {required && (
                        <Text component="span" tone="error">
                            {' '}
                            *
                        </Text>
                    )}
                </Label>
                <IconButton
                    icon={HelpOutline}
                    onClick={() => setHelpOpen(true)}
                    tooltip="How do I find this?"
                    size={24}
                    iconSize={16}
                />
            </Row>
            {children}
            {helpOpen && (
                <Modal title={`How to find: ${label}`} onClose={() => setHelpOpen(false)} width={760}>
                    <Stack gap="md">
                        {typeof help === 'string' ? <Text tone="secondary">{help}</Text> : help}
                        <ScreenshotFrame>
                            {image ? <ScreenshotImg src={image} alt={label} /> : 'Screenshot goes here'}
                        </ScreenshotFrame>
                    </Stack>
                </Modal>
            )}
        </Stack>
    );
};

const ScreenshotFrame = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 220,
    borderRadius: 10,
    border: `1px dashed ${Colors.border}`,
    backgroundColor: Colors.surface,
    color: Colors.textSecondary,
    overflow: 'hidden',
});

const ScreenshotImg = styled('img')({
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
});

export default function VerifyEligibilityModal({
    workerId,
    workerName,
    submitVerification,
    onClose,
    submitLabel = 'Book',
    eligibleOutcomeCopy,
}: {
    workerId: number;
    workerName: string;
    submitVerification: (form: VerificationForm) => Promise<BookResult | null>;
    onClose: (result?: BookResult) => void;
    submitLabel?: string;
    eligibleOutcomeCopy?: string;
}) {
    const [revealOpen, setRevealOpen] = useState(false);
    const [eligible, setEligible] = useState<string | null>(null);
    const [dcfStatus, setDcfStatus] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [initiateReview, setInitiateReview] = useState<string | null>(null);
    const [determinationDate, setDeterminationDate] = useState('');
    const [printsExpirationDate, setPrintsExpirationDate] = useState('');
    const [clearinghouseRecord, setClearinghouseRecord] = useState<File>();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<BookResult>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const today = startOfDay(new Date());
    const determinationDateInFuture = !!determinationDate && isAfter(startOfDay(parseISO(determinationDate)), today);
    const determinationDateIsRecent =
        !!determinationDate &&
        !determinationDateInFuture &&
        isAfter(startOfDay(parseISO(determinationDate)), subDays(today, 15));
    const printsExpirationDateInPast =
        !!printsExpirationDate && isBefore(startOfDay(parseISO(printsExpirationDate)), today);
    const printsExpiringSoon =
        !!printsExpirationDate &&
        !printsExpirationDateInPast &&
        isBefore(startOfDay(parseISO(printsExpirationDate)), addMonths(today, 1));
    const datesWithinAYear =
        !!determinationDate &&
        !!printsExpirationDate &&
        isBefore(startOfDay(parseISO(printsExpirationDate)), addYears(startOfDay(parseISO(determinationDate)), 1));

    const requiresReview = dcfStatus != null && STATUS_REQUIRING_REVIEW.includes(dcfStatus);
    const requiresExpiration = dcfStatus != null && STATUSES_REQUIRING_EXPIRATION.includes(dcfStatus);
    const reviewName = dcfStatus != null ? REVIEW_NAMES[dcfStatus] : undefined;
    const noFlowComplete =
        eligible === 'no' &&
        (notFound ||
            (dcfStatus != null &&
                (!requiresExpiration || !!printsExpirationDate) &&
                (!requiresReview || initiateReview != null)));
    const yesFlowComplete =
        eligible === 'yes' &&
        !!determinationDate &&
        !!printsExpirationDate &&
        !determinationDateInFuture &&
        !printsExpirationDateInPast;

    function onEligibleChange(value: string) {
        setEligible(value);
        setDcfStatus(null);
        setNotFound(false);
        setInitiateReview(null);
        setPrintsExpirationDate('');
    }

    function onDcfStatusChange(value: string) {
        setDcfStatus(value);
        setNotFound(false);
        setInitiateReview(null);
        if (!STATUSES_REQUIRING_EXPIRATION.includes(value)) {
            setPrintsExpirationDate('');
        }
    }

    function onNotFound() {
        setNotFound(true);
        setDcfStatus(null);
        setInitiateReview(null);
        setPrintsExpirationDate('');
    }

    const [firstName, ...lastNameParts] = workerName.split(' ');
    const provider = {
        id: workerId,
        first_name: firstName,
        last_name: lastNameParts.join(' '),
    } as ShiftProviderUser;

    async function onRevealInfo() {
        try {
            setRevealOpen(true);
            await client(`api/business/reveal-code/`, { method: 'POST', body: { provider: provider.id } });
        } catch (error) {
            setRevealOpen(false);
            alert(extractErrorMessage(error, 'Cannot send SSN reveal code'));
        }
    }

    function book() {
        setLoading(true);
        const form: VerificationForm =
            eligible === 'no'
                ? {
                      eligibility_date: '',
                      expiration_date: requiresExpiration ? printsExpirationDate : '',
                      screening_document: notFound ? undefined : clearinghouseRecord,
                      clearinghouse_status: notFound ? 'Not found' : 'Yes',
                      eligibility_status: notFound ? undefined : dcfStatus ?? undefined,
                      initiated_review: requiresReview && initiateReview === 'yes',
                  }
                : {
                      eligibility_date: determinationDate,
                      expiration_date: printsExpirationDate,
                      screening_document: clearinghouseRecord,
                      clearinghouse_status: 'Yes',
                      eligibility_status: 'eligible',
                  };
        submitVerification(form)
            .then((bookResult) => {
                if (eligible === 'no') {
                    onClose();
                } else if (bookResult) {
                    setResult(bookResult);
                } else {
                    onClose();
                }
            })
            .catch(consoleLogInDev)
            .finally(() => setLoading(false));
    }

    if (result) {
        return <BookResultModal workerName={workerName} result={result} onClose={() => onClose(result)} />;
    }

    const clearinghouseRecordUploadField = (
        <LabeledField
            label="Clearinghouse Record"
            help={
                <Stack gap="sm">
                    <Text tone="secondary">
                        Press Print in the top-right corner of the worker&rsquo;s Clearinghouse record, then save it as
                        a PDF and upload the file here.
                    </Text>
                    <Text tone="secondary">
                        <b>On a Mac:</b> in the print window, choose Save to Preview (or open the PDF dropdown and pick
                        Save as PDF), then save the file.
                    </Text>
                    <Text tone="secondary">
                        <b>On Windows:</b> in the print window, set the printer to Microsoft Print to PDF (or Save as
                        PDF), then click Print and save the file.
                    </Text>
                </Stack>
            }
            image="https://cdn.join-tandem.com/media/tandem_static/print_results_instructions.png"
        >
            <Row gap="sm">
                <PillButton variant="secondary" onClick={() => fileInputRef.current?.click()}>
                    {clearinghouseRecord ? 'Replace file' : 'Upload file'}
                </PillButton>
                {clearinghouseRecord && <Text tone="secondary">{clearinghouseRecord.name}</Text>}
            </Row>
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
                onChange={(e) => setClearinghouseRecord(e.target.files?.[0])}
            />
        </LabeledField>
    );

    return (
        <Modal title={`Verify ${workerName}'s Eligibility to Book`} onClose={() => onClose()} width={560}>
            <Stack gap="md">
                <Stack gap="sm">
                    <Title size="h2">1. Check the Clearinghouse</Title>
                    <Text tone="secondary">
                        Use the information below to look up {workerName} in the DCF Background Screening{' '}
                        <Link href={CLEARINGHOUSE_URL}>Clearinghouse</Link>. Confirm that their status shows they are
                        eligible for child care.
                    </Text>
                    <Row>
                        <PillButton variant="secondary" onClick={onRevealInfo}>
                            View Worker Information
                        </PillButton>
                    </Row>
                </Stack>
                <RevealModal open={revealOpen} onClose={() => setRevealOpen(false)} provider={provider} minimal />
                <Divider />
                <Title size="h2">2. Confirm Eligibility</Title>
                <LabeledField
                    label={`Is ${workerName} eligible for DCF Child Care?`}
                    help="On the worker's Clearinghouse record, check that their DCF Child Care status shows they are eligible. If it does not say eligible, they are not eligible for DCF Child Care."
                    image="https://cdn.join-tandem.com/media/tandem_static/child_care_eligibility_instructions.png"
                    required
                >
                    <RadioGroup
                        options={[
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                        ]}
                        value={eligible}
                        onChange={onEligibleChange}
                    />
                </LabeledField>
                {eligible === 'no' && (
                    <Stack gap="md">
                        <LabeledField
                            label="What does DCF Child Care currently say?"
                            help="On the worker's Clearinghouse record, find their DCF Child Care row and select the status shown there."
                            image="https://cdn.join-tandem.com/media/tandem_static/child_care_eligibility_instructions.png"
                            required
                        >
                            <Select
                                options={DCF_STATUS_OPTIONS}
                                value={dcfStatus ?? ''}
                                onChange={onDcfStatusChange}
                                placeholder="Select a status"
                                fullWidth
                            />
                            <Link onClick={onNotFound}>The worker did not come up in Clearinghouse</Link>
                        </LabeledField>
                        {notFound && (
                            <Text tone="secondary">
                                You reported that {workerName} did not come up in the Clearinghouse.
                            </Text>
                        )}
                        {requiresExpiration && (
                            <LabeledField
                                label="Retained Prints Expiration Date"
                                help="Find the Retained Prints Expiration Date on the worker's Clearinghouse record in the top right corner, then input the date."
                                image="https://cdn.join-tandem.com/media/tandem_static/retained_prints_instructions.png"
                                required
                            >
                                <DatePicker value={printsExpirationDate} onChange={setPrintsExpirationDate} fullWidth />
                            </LabeledField>
                        )}
                        {requiresReview && (
                            <Stack gap="sm">
                                <Label>
                                    Do you want to initiate the {reviewName}?
                                    <Text component="span" tone="error">
                                        {' '}
                                        *
                                    </Text>
                                </Label>
                                <RadioGroup
                                    options={[
                                        { value: 'yes', label: 'Yes' },
                                        { value: 'no', label: 'No' },
                                    ]}
                                    value={initiateReview}
                                    onChange={setInitiateReview}
                                />
                                {initiateReview === 'yes' && (
                                    <Stack gap="sm">
                                        <Text tone="secondary">
                                            You can initiate the {reviewName} directly in the{' '}
                                            <Link href={CLEARINGHOUSE_URL}>Clearinghouse</Link>. Once you&rsquo;ve
                                            submitted it there, press &ldquo;I initiated the {reviewName}&rdquo; below
                                            and we&rsquo;ll note it&rsquo;s in process. When the screening comes back as
                                            Eligible, return to Tandem to book the substitute.
                                        </Text>
                                        {dcfStatus === 'resubmission required' && (
                                            <Text tone="secondary">
                                                The Resubmission fee is $12. Tandem will reimburse the $12 fee as a
                                                credit on your next invoice.
                                            </Text>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        )}
                        {dcfStatus != null && !notFound && clearinghouseRecordUploadField}
                        {noFlowComplete && (
                            <Text tone="error">
                                You cannot currently book {workerName}.{' '}
                                {initiateReview === 'yes'
                                    ? `Only press "I initiated the ${reviewName}" after you've submitted it in the Clearinghouse.`
                                    : `There is no further action available to you yet — press "Submit" to save what you found so our records stay up to date.`}
                            </Text>
                        )}
                    </Stack>
                )}
                {eligible === 'yes' && (
                    <Stack gap="md">
                        <LabeledField
                            label="DCF Child Care Eligibility Determination Date"
                            help="Find this on the worker's Clearinghouse record for DCF Child Care, then input the date."
                            image="https://cdn.join-tandem.com/media/tandem_static/child_care_eligibility_date_instructions.png"
                            required
                        >
                            <DatePicker
                                value={determinationDate}
                                onChange={setDeterminationDate}
                                maxDate={today}
                                fullWidth
                            />
                            {determinationDateInFuture && (
                                <Text tone="error">
                                    The eligibility determination date cannot be in the future. Enter the date shown on
                                    the worker&rsquo;s Clearinghouse record.
                                </Text>
                            )}
                            <Collapse in={determinationDateIsRecent} unmountOnExit>
                                <Alert variant="warning">
                                    <Text size="xs">
                                        It looks like they became eligible recently. Double check you entered it
                                        correctly.
                                    </Text>
                                </Alert>
                            </Collapse>
                        </LabeledField>
                        <LabeledField
                            label="Retained Prints Expiration Date"
                            help="Find the Retained Prints Expiration Date on the worker's Clearinghouse record in the top right corner, then input the date."
                            image="https://cdn.join-tandem.com/media/tandem_static/retained_prints_instructions.png"
                            required
                        >
                            <DatePicker
                                value={printsExpirationDate}
                                onChange={setPrintsExpirationDate}
                                minDate={today}
                                fullWidth
                            />
                            {printsExpirationDateInPast && (
                                <Text tone="error">
                                    The retained prints expiration date cannot be in the past. If the prints have
                                    already expired, {workerName} is not eligible to book.
                                </Text>
                            )}
                            <Collapse in={printsExpiringSoon} unmountOnExit>
                                <Alert variant="warning">
                                    <Text size="xs">
                                        This substitute&apos;s check is expiring soon. Double check you entered it in
                                        correctly.
                                    </Text>
                                </Alert>
                            </Collapse>
                        </LabeledField>
                        <Collapse in={datesWithinAYear} unmountOnExit>
                            <Alert variant="warning">
                                <Text size="xs">
                                    These two dates are less than a year apart. Double check you entered both of them
                                    correctly.
                                </Text>
                            </Alert>
                        </Collapse>
                        {clearinghouseRecordUploadField}
                    </Stack>
                )}
                <Divider />
                {eligible !== 'no' && (
                    <Text tone="secondary">
                        {eligibleOutcomeCopy ? (
                            <>{eligibleOutcomeCopy} </>
                        ) : (
                            <>Once you confirm that {workerName} is eligible, we&rsquo;ll add them to your schedule. </>
                        )}
                        If they go 90 days without working at your center, you&rsquo;ll need to verify their eligibility
                        again.
                    </Text>
                )}
                <Row justify="end" fullWidth>
                    <PillButton disabled={(!yesFlowComplete && !noFlowComplete) || loading} onClick={book}>
                        {loading
                            ? 'Submitting...'
                            : eligible === 'no'
                              ? initiateReview === 'yes'
                                  ? `I initiated the ${reviewName}`
                                  : 'Submit'
                              : submitLabel}
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}
